import { apiClient } from './apiClient';
import type { WatchTopic } from '../types';
import type { FeedItem } from '../types/feedItem';
import { feedItemsService } from './feedItemsService';
import { Logger } from '../utils/logger';

export interface GenerateKeywordsResponse {
  keywords: string[];
  reasoning: string;
}

/**
 * Suggestion de centre d'intérêt générée par l'IA (côté backend, route /watch/suggest-topics).
 * On reçoit la liste, l'utilisateur coche celles qu'il veut, et on les persiste ensuite
 * via les routes /watch/ classiques (un POST par topic accepté).
 */
export interface SuggestedTopic {
  name: string;
  description: string;
  keywords: string[];
}

export interface SuggestTopicsResponse {
  topics: SuggestedTopic[];
}

interface BackendWatchTopic {
  id: string;
  prompt?: string;
  name?: string;
  keywords?: string[];
  enabled?: boolean;
  is_active?: boolean;
  created_at?: string;
  items_count?: number;
}

type BackendTopicRecord = Record<string, unknown>;

export const watchTopicsService = {
  getAll: async (spaceId: string): Promise<WatchTopic[]> => {
    Logger.debug('[WatchTopicsService] Fetching watch topics', { spaceId });
    const response = await apiClient.get<BackendWatchTopic[]>('/watch/', {
      params: { space_id: spaceId }
    });
    const topics = response.data.map(mapBackendTopicToFrontend);
    Logger.info('[WatchTopicsService] Watch topics fetched', { spaceId, count: topics.length });
    return topics;
  },

  create: async (spaceId: string, topic: Partial<WatchTopic>) => {
    Logger.info('[WatchTopicsService] Creating watch topic', {
      spaceId,
      hasPrompt: !!topic.prompt,
      keywordsCount: topic.keywords?.length ?? 0,
    });
    const payload = mapFrontendTopicToBackend(topic, spaceId);
    const response = await apiClient.post<BackendWatchTopic>('/watch/', payload);
    const createdTopic = mapBackendTopicToFrontend(response.data);
    Logger.info('[WatchTopicsService] Watch topic created', { topicId: createdTopic.id, spaceId });
    return createdTopic;
  },

  update: async (id: string, topicData: Partial<WatchTopic>) => {
    Logger.info('[WatchTopicsService] Updating watch topic', {
      topicId: id,
      hasPrompt: topicData.prompt !== undefined,
      hasKeywords: topicData.keywords !== undefined,
      hasEnabled: topicData.enabled !== undefined,
    });
    const payload = mapFrontendTopicToBackend(topicData);
    const response = await apiClient.put<BackendWatchTopic>(`/watch/${id}`, payload);
    const updatedTopic = mapBackendTopicToFrontend(response.data);
    Logger.info('[WatchTopicsService] Watch topic updated', { topicId: updatedTopic.id });
    return updatedTopic;
  },

  delete: async (id: string) => {
    Logger.warn('[WatchTopicsService] Deleting watch topic', { topicId: id });
    await apiClient.delete(`/watch/${id}`);
    Logger.info('[WatchTopicsService] Watch topic deleted', { topicId: id });
    return { success: true };
  },

  /**
   * Suggère 3-5 centres d'intérêt à partir du positionnement du space.
   *
   * Le backend lit le `positioning_context` du space en BDD et appelle le LLM.
   * Côté frontend, on affiche ces propositions dans une modale (cf SuggestTopicsModal)
   * où l'utilisateur peut cocher/décocher avant de persister via `create()`.
   */
  suggestTopics: async (spaceId: string): Promise<SuggestTopicsResponse> => {
    Logger.info('[WatchTopicsService] Suggesting topics from positioning', { spaceId });
    const response = await apiClient.post<SuggestTopicsResponse>(
      '/watch/suggest-topics',
      undefined,
      { params: { space_id: spaceId } },
    );
    Logger.info('[WatchTopicsService] Topics suggested', { count: response.data.topics?.length ?? 0 });
    return response.data;
  },

  /**
   * Génère des mots-clés via IA pour un prompt donné
   */
  generateKeywords: async (prompt: string): Promise<GenerateKeywordsResponse> => {
    Logger.info('[WatchTopicsService] Generating keywords', { promptLength: prompt.length });
    const response = await apiClient.post<GenerateKeywordsResponse>('/watch/generate-keywords', {
      prompt
    });
    Logger.info('[WatchTopicsService] Keywords generated', { keywordsCount: response.data.keywords?.length ?? 0 });
    return response.data;
  },

  /**
   * Get feed items (web search results) associated with a watch topic.
   */
  getItems: async (topicId: string, limit = 50): Promise<FeedItem[]> => {
    Logger.debug('[WatchTopicsService] Fetching topic feed items', { topicId, limit });
    const result = await feedItemsService.getByTopicId(topicId, { limit });
    return result.items;
  },

  /**
   * Get the count of feed items for a watch topic.
   */
  getItemsCount: async (topicId: string): Promise<number> => {
    Logger.debug('[WatchTopicsService] Fetching topic feed items count', { topicId });
    return feedItemsService.getTopicItemsCount(topicId);
  },
};

function mapBackendTopicToFrontend(data: BackendWatchTopic): WatchTopic {
  return {
    id: data.id,
    prompt: data.prompt || data.name, // Handle prompt/name
    keywords: data.keywords || [],
    enabled: data.enabled ?? data.is_active,
    createdDate: data.created_at ? new Date(data.created_at) : undefined,
    itemCount: data.items_count,
  } as WatchTopic;
}

function mapFrontendTopicToBackend(topic: Partial<WatchTopic>, spaceId?: string): BackendTopicRecord {
  const data: BackendTopicRecord = {};
  if (spaceId) data.space_id = spaceId;
  if (topic.prompt !== undefined) data.prompt = topic.prompt;
  if (topic.keywords !== undefined) data.keywords = topic.keywords;
  if (topic.enabled !== undefined) data.enabled = topic.enabled;
  
  return data;
}

